/**
 * Sidepanel width / height / vertical offset, remembered per monitor.
 *
 * Two layers, because "the user sized the panel here" and "the user sized the
 * panel at all" are different facts:
 *
 * - `displays[key]` holds only what the user explicitly chose *on that
 *   monitor*. Moving back to it restores exactly that.
 * - `inherited` holds the last value the user chose for each field on any
 *   monitor. A display the user has never sized takes these, so dragging the
 *   panel to a second screen brings its shape along instead of snapping to the
 *   built-in defaults.
 *
 * The fallback is per *field*, not per display: sizing only the height on a new
 * monitor must not drop the width the panel was already wearing when it got
 * there.
 *
 * Nothing here clamps — the resolved values are still just preferences, and the
 * destination display's bounds are applied downstream (`clampSidepanelWidth`,
 * `resolveSidepanelVerticalLayout`). Keeping the clamp out of storage is what
 * lets a tall panel survive a trip through a short monitor.
 */

export const SIDEPANEL_GEOMETRY_SETTING_KEY = 'sidepanel.geometry'

/** Legacy display-independent keys, folded into `inherited` by `migrateOverlayGeometryPerDisplay`. */
export const LEGACY_SIDEPANEL_WIDTH_SETTING_KEY = 'sidepanel.customWidth'
export const LEGACY_SIDEPANEL_HEIGHT_SETTING_KEY = 'sidepanel.customHeight'
export const LEGACY_SIDEPANEL_VERTICAL_OFFSET_SETTING_KEY = 'sidepanel.verticalOffset'

/** `null` is a choice too — "back to the default" (double-click to reset/center). */
export type SidepanelGeometryValue = number | null

export interface SidepanelDisplayGeometry {
  width?: SidepanelGeometryValue
  height?: SidepanelGeometryValue
  verticalOffset?: SidepanelGeometryValue
}

export interface SidepanelGeometryStore {
  /** Keyed by `overlayDisplayKey`. Only monitors the user has sized on. */
  displays?: Record<string, SidepanelDisplayGeometry>
  inherited?: SidepanelDisplayGeometry
}

export interface ResolvedSidepanelGeometry {
  width: number | null
  height: number | null
  verticalOffset: number | null
}

const GEOMETRY_FIELDS = ['width', 'height', 'verticalOffset'] as const

type GeometryField = (typeof GEOMETRY_FIELDS)[number]

const isGeometryValue = (value: unknown): value is SidepanelGeometryValue =>
  value === null || (typeof value === 'number' && Number.isFinite(value))

/** Drops anything a corrupt or hand-edited store could hold. */
const sanitize = (geometry: SidepanelDisplayGeometry | undefined): SidepanelDisplayGeometry => {
  const clean: SidepanelDisplayGeometry = {}
  if (!geometry || typeof geometry !== 'object') {
    return clean
  }
  for (const field of GEOMETRY_FIELDS) {
    const value = geometry[field]
    if (isGeometryValue(value)) {
      clean[field] = value
    }
  }
  return clean
}

export function resolveSidepanelGeometry(
  store: SidepanelGeometryStore | undefined,
  displayKey: string | null,
): ResolvedSidepanelGeometry {
  const own = sanitize(displayKey ? store?.displays?.[displayKey] : undefined)
  const inherited = sanitize(store?.inherited)
  // `undefined` is "never chosen" and falls through; `null` is a chosen reset
  // and stops here, so double-clicking to re-center on one monitor doesn't
  // immediately re-inherit the offset it just cleared.
  const read = (field: GeometryField): number | null => {
    if (own[field] !== undefined) {
      return own[field] ?? null
    }
    return inherited[field] ?? null
  }

  return { width: read('width'), height: read('height'), verticalOffset: read('verticalOffset') }
}

/**
 * Records an explicit choice: on this display, and as what a display the user
 * has never sized should inherit.
 */
export function applySidepanelGeometry(
  store: SidepanelGeometryStore | undefined,
  displayKey: string | null,
  patch: SidepanelDisplayGeometry,
): SidepanelGeometryStore {
  const clean = sanitize(patch)
  if (Object.keys(clean).length === 0) {
    return store ?? {}
  }

  const inherited = { ...sanitize(store?.inherited), ...clean }
  if (!displayKey) {
    return { ...store, inherited }
  }

  return {
    ...store,
    displays: {
      ...store?.displays,
      [displayKey]: { ...sanitize(store?.displays?.[displayKey]), ...clean },
    },
    inherited,
  }
}
